/**
 * src/api/billingApi.ts — Caller-scoped Stripe billing endpoints.
 *
 * Server-side resources live under `resources/Stripe*.js`. The browser
 * never touches secret keys; raw card data is captured by Stripe Elements
 * and confirmed against the SetupIntent's client_secret directly with
 * Stripe — never through our server.
 */

import { post, getOne, getCollection, type Collection } from './harperClient';

export type PaymentMethodStatus =
  | 'none'
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'canceled';

export interface PaymentMethodSummary {
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  payment_method_status: PaymentMethodStatus;
  /** ISO timestamp set when the first invoice failure landed; null
   *  when the customer isn't past_due. Powers the 48h-grace clock
   *  computed by src/utils/billingStatus.ts. */
  past_due_since: string | null;
}

export interface SetupIntentResponse {
  client_secret: string;
  customer_id: string;
}

/**
 * Issue a Stripe SetupIntent for the signed-in customer. The returned
 * client_secret is what `stripe.confirmSetup` from @stripe/stripe-js
 * needs on the browser side.
 */
export async function createSetupIntent(): Promise<SetupIntentResponse> {
  return post<SetupIntentResponse>('/StripeSetupIntent', {});
}

/**
 * After the browser successfully confirms a SetupIntent, hand the
 * SetupIntent id back to the server. Server verifies status='succeeded'
 * with Stripe, then attaches the resulting PaymentMethod as the
 * customer's default. Returns the new card summary.
 */
export async function attachPaymentMethod(setupIntentId: string): Promise<PaymentMethodSummary> {
  return post<PaymentMethodSummary>('/StripePaymentMethod', { setup_intent_id: setupIntentId });
}

/**
 * Read the caller's current default card summary. Returns nullish fields
 * when no card is on file. payment_method_status is always populated
 * (defaults to 'none').
 */
export async function getPaymentMethod(): Promise<PaymentMethodSummary> {
  const res = await getOne<PaymentMethodSummary>('/StripePaymentMethod');
  if (!res) {
    return {
      brand: null, last4: null, exp_month: null, exp_year: null,
      payment_method_status: 'none', past_due_since: null,
    };
  }
  return res;
}

/**
 * ALPHA-ONLY: auto-attach Stripe's `pm_card_visa` test PaymentMethod to
 * the caller's Stripe Customer in one round-trip. Powers AlphaCardModal.
 * No Elements needed — the server creates + confirms a SetupIntent with
 * the pre-built test token. Returns the same summary shape as
 * attachPaymentMethod / getPaymentMethod.
 *
 * Post-alpha: this client function (and the matching /StripeTestCard
 * resource) get removed; UI swaps back to attachPaymentMethod via the
 * Elements modal.
 */
export async function attachAlphaTestCard(): Promise<PaymentMethodSummary> {
  return post<PaymentMethodSummary>('/StripeTestCard', {});
}

export interface BillingPreview {
  /** True for the first-crate scenario — no sub yet, immediate $15×qty charge. */
  first_charge: boolean;
  /** Signed cents. Positive = additional charge; negative = credit. */
  proration_amount_cents: number;
  /** ISO date the change settles (today for first_charge, anchor day otherwise). */
  next_charge_at: string | null;
  /** Upcoming-invoice total after the change, in cents. */
  next_total_cents: number | null;
  /** Sub.quantity after this change is applied. */
  new_quantity: number;
}

/**
 * Preview the billing impact of a hypothetical quantity change before
 * the customer commits. Use delta = +N for "what would adding N crates
 * cost?" and delta = -N for "what would removing N crates credit?"
 *
 * Read-only on the server (no DB or Stripe writes). Safe to call as
 * the customer adjusts a quantity, though debounce is recommended to
 * avoid Stripe rate-limit pressure.
 */
export async function previewBillingDelta(delta: number): Promise<BillingPreview> {
  return post<BillingPreview>('/StripeBillingPreview', { delta });
}

export interface InvoiceRecord {
  id: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: string | null;
  period_start: string | null;
  period_end: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export interface SubscriptionSummary {
  id: string;
  status: string;
  quantity: number | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  latest_invoice_amount_due: number | null;
  latest_invoice_status: string | null;
}

/**
 * Fetch the caller's last 24 Stripe invoices (live, not cached).
 */
export async function listInvoices(): Promise<InvoiceRecord[]> {
  const res: Collection<InvoiceRecord> = await getCollection<InvoiceRecord>('/StripeInvoices');
  return res.data ?? [];
}

/**
 * Fetch the caller's current subscription summary (live from Stripe),
 * or null if none exists.
 */
export async function getSubscription(): Promise<SubscriptionSummary | null> {
  return getOne<SubscriptionSummary>('/StripeSubscription');
}

/**
 * Open a Stripe-hosted Customer Portal session for the caller. Returns
 * a single-use redirect URL the client can navigate to.
 */
export async function createPortalSession(returnUrl?: string): Promise<{ url: string }> {
  const body = returnUrl ? { return_url: returnUrl } : {};
  return post<{ url: string }>('/StripePortalSession', body);
}
